/**
 * Which stages a piece can be sent back to (Request revision).
 *
 * Shared by the SPA's RevisionModal (which stages it offers) and the API's
 * POST /pieces/:id/decision (which it accepts) — FEAT-108 / S-438. A target is
 * reachable once its stage has completed, so a piece cannot be "revised" back
 * to a stage it never reached.
 *
 * Non-fiction only: fiction pieces have no revision targets.
 */
import { MAIN_PIPELINE_PHASES } from "./fiction-phases.js";

/** Revision targets, closest to the end of the pipeline first. */
export const REVISION_STAGE_ORDER = [
  "art_director",
  "seo_optimizer",
  "editor",
  "auditor",
  "writer",
  "librarian",
  "scout",
] as const;

/** The generation_phases value that marks each target's stage as completed. */
export const STAGE_PHASE_VALUE: Record<string, string> = {
  art_director: "phase_7_art_directed",
  seo_optimizer: "phase_6_seo_optimized",
  editor: "phase_5_corrected",
  auditor: "phase_4_fact_checked",
  writer: "phase_3_draft_generated",
  librarian: "phase_2_outlined",
  scout: "phase_1_researched",
};

const PHASE_ORDER: readonly string[] = MAIN_PIPELINE_PHASES.map((p) => p.value);

/**
 * Index of the piece's last-completed phase, or -1 when nothing has run yet
 * (generation_phases null) or the value isn't recognized.
 */
export function completedPhaseIndex(generationPhases: string | null | undefined): number {
  if (!generationPhases) return -1;
  return PHASE_ORDER.indexOf(generationPhases);
}

export function isStageReachable(stageValue: string, generationPhases: string | null | undefined): boolean {
  const phaseIdx = PHASE_ORDER.indexOf(STAGE_PHASE_VALUE[stageValue] ?? "");
  return phaseIdx !== -1 && phaseIdx <= completedPhaseIndex(generationPhases);
}

/** The reachable targets, closest-to-current first. */
export function reachableStageValues(generationPhases: string | null | undefined): string[] {
  return REVISION_STAGE_ORDER.filter((s) => isStageReachable(s, generationPhases));
}
